import type { BillingCreditsStatementLineItem } from '@st/sdk'
import { Button, Modal } from '@st/theme'
import { inflect } from '@st/util/inflect'
import { useState } from 'react'
import { match } from '@st/pattern'
import { PurchaseCreditsDialog } from '~/features/billing'
import { OrganizationSettingsScaffold } from '../st-organizations/st-organization-settings-scaffold'
import { useOrganizationBilling } from './hooks'
import { OrganizationBillingLoadFailed } from './organization-billing-load-failed'

export function OrganizationBalancePage() {
  const [state, send] = useOrganizationBilling()

  const [dialog, setDialog] = useState<'purchase_credits' | undefined>()

  return (
    <OrganizationSettingsScaffold title="Settings">
      <div className="max-w-3xl">
        <h2 className="mb-5 text-xl text-gray-800">Balance</h2>

        {match(state)
          .with({ status: 'loadingBilling' }, () => null)
          .with({ status: 'loadFailed' }, () => (
            <OrganizationBillingLoadFailed onRetry={() => send({ type: 'retryBillingLoad' })} />
          ))
          .with({ status: 'loaded' }, ({ billingState }) => {
            return (
              <div className="flex flex-col gap-8">
                <div>
                  <h1 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-800">
                    Current Balance
                  </h1>
                  <CreditsBalanceCard
                    balance={billingState.creditsStatement.currentBalance}
                    onPurchaseCreditsClick={() => setDialog('purchase_credits')}
                  />
                </div>
                <div>
                  <h1 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-800">
                    Credit history
                  </h1>
                  <CreditsHistoryTable lineItems={billingState.creditsStatement.lineItems} />
                </div>
              </div>
            )
          })
          .otherwise(() => null)}
      </div>
      {match(dialog)
        .with('purchase_credits', () => {
          return (
            <Modal isOpen={true} onClose={() => setDialog(undefined)}>
              <PurchaseCreditsDialog
                onClose={() => setDialog(undefined)}
              />
            </Modal>
          )
        })
        .otherwise(() => null)}
    </OrganizationSettingsScaffold>
  )
}

type CreditsBalanceCardProps = {
  balance: number
  onPurchaseCreditsClick?: () => void
}

function CreditsBalanceCard({ balance, onPurchaseCreditsClick }: CreditsBalanceCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-md">
      <div className="flex items-center justify-between p-6">
        <div className="text-xl font-bold text-gray-900">
          {balance} {inflect(balance, 'credit')}
        </div>
        {onPurchaseCreditsClick && (
          <Button variant="primary" onClick={onPurchaseCreditsClick}>
            Purchase Credits
          </Button>
        )}
      </div>
    </div>
  )
}

type CreditsHistoryTableProps = {
  lineItems: BillingCreditsStatementLineItem[]
}

function CreditsHistoryTable({ lineItems }: CreditsHistoryTableProps) {
  if (lineItems.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-gray-500 shadow-md">
        No billing history found.
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-md">
      <div className="p-6">
        <div className="max-h-96 overflow-x-auto overflow-y-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-300">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="h-9 border-b border-gray-300 bg-white">
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-xs uppercase text-gray-600 sm:pl-6"
                >
                  Date
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-xs uppercase text-gray-600">
                  Description
                </th>
                <th
                  scope="col"
                  className="py-3.5 pl-3 pr-4 text-right text-xs uppercase text-gray-600"
                >
                  Amount
                </th>
                <th
                  scope="col"
                  className="py-3.5 pl-3 pr-4 text-right text-xs uppercase text-gray-600 sm:pr-6"
                >
                  Balance
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {lineItems.map((item, index) => (
                <tr key={index} className="h-12">
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500 sm:pl-6">
                    {new Date(item.date).toLocaleDateString()}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {item.description}
                  </td>
                  <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm tabular-nums text-gray-900">
                    {item.amount}
                  </td>
                  <td
                    className={`whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm tabular-nums sm:pr-6 ${
                      item.balanceSnapshot < 0 ? 'text-red-600' : 'text-gray-900'
                    }`}
                  >
                    {item.balanceSnapshot}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
